import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import { useTheme, useMediaQuery, Select, MenuItem } from "@mui/material";
import {
  AssistWalkerOutlined,
  BusinessSharp,
  Dashboard,
  MonetizationOnOutlined,
  PeopleOutline,
  Person,
  Inventory2Outlined,
  LabelOutlined,
  AssignmentOutlined,
  DirectionsCarOutlined,
  SmartphoneOutlined,
  PolicyOutlined,
  HistoryOutlined,
} from "@mui/icons-material";
import { tokens } from "../../theme";
import * as defaultComponentsMap from "../../scenes/Component/VericalTabComponents";

// Brand blue matches the accent already established in dynamicTableForJson.js
const BRAND_BLUE = "#1766A2";

// Maps known backend subright titles to an icon. Anything unmatched falls
// back to a generic icon rather than rendering with no icon at all, so the
// rail stays visually consistent even for titles we haven't explicitly mapped.
const getIconByName = (title) => {
  switch (title) {
    case "Customer Dashboard":
      return <Dashboard fontSize="small" />;
    case "Next Of Kin":
      return <PeopleOutline fontSize="small" />;
    case "Guarantor":
      return <AssistWalkerOutlined fontSize="small" />;
    case "Businnes Details":
      return <BusinessSharp fontSize="small" />;
    case "BioData":
      return <Person fontSize="small" />;
    case "LoanData":
      return <MonetizationOnOutlined fontSize="small" />;
    case "Collateral Assets":
    case "Customer Collateral":
    case "Collateral Documents":
    case "Collateral":
      return <Inventory2Outlined fontSize="small" />;
    case "Document Requirement":
    case "Document Requirements":
    case "Registry":
    case "Registries":
      return <AssignmentOutlined fontSize="small" />;
    case "Valuer Firm":
    case "Valuer Firms":
      return <BusinessSharp fontSize="small" />;
    case "Car Models":
    case "Car Model":
    case "CarModels":
    case "Car Makes":
    case "Car Make":
    case "CarMakes":
      return <DirectionsCarOutlined fontSize="small" />;
    case "Customer Devices":
    case "Customer Device":
    case "CustomerDevices":
    case "Device Lists":
    case "Device List":
    case "DeviceLists":
    case "DeviceList":
    case "Device Registry":
    case "DeviceRegistry":
    case "Devices":
    case "Device Setups":
    case "DeviceSetups":
    case "All Devices":
      return <SmartphoneOutlined fontSize="small" />;
    case "Device Lock Policies":
    case "Device Lock Policy":
    case "DeviceLockPolicies":
    case "DeviceLockPolicy":
    case "Device Policies":
    case "Device Policy":
      return <PolicyOutlined fontSize="small" />;
    case "Device Lock Actions":
    case "DeviceLockActions":
    case "Device Actions":
    case "DeviceActions":
      return <HistoryOutlined fontSize="small" />;
    default:
      return <LabelOutlined fontSize="small" />;
  }
};

function TabPanel({ children, value, index }) {
  if (value !== index) return null;
  return (
    <Box
      role="tabpanel"
      id={`vertical-tabpanel-${index}`}
      aria-labelledby={`vertical-tab-${index}`}
      sx={{ width: "100%" }}
    >
      {children}
    </Box>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired,
};

function a11yProps(index) {
  return {
    id: `vertical-tab-${index}`,
    "aria-controls": `vertical-tabpanel-${index}`,
  };
}

/**
 * Shared vertical tab rail. `componentsMap` defaults to VericalTabComponents
 * (Setup Management / Company Management / HR setups / etc.) but any module
 * of named { label: Component } exports can be passed in — e.g. the customer
 * 360 / HR self-service screens pass HorizontalTabComponents with
 * `showIcons` to get the same rail with icons instead of a separate
 * horizontal, paginated tab bar.
 */
const DynamicTabs = ({ rightId, componentsMap, showIcons }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isCompact = isSmallScreen || isMediumScreen;

  const [value, setValue] = useState(0);
  const [subRightComponents, setSubRightComponents] = useState([]);
  const rights = JSON.parse(localStorage.getItem("rights"));
  const map = componentsMap || defaultComponentsMap;

  useEffect(() => {
    if (!rights) return;
    try {
      const cleanedData = JSON.parse(rights.replace(/\\/g, ""));

      if (cleanedData && Array.isArray(cleanedData)) {
        const setupMgmt = cleanedData.find(
          (m) => m.title?.toLowerCase().replace(/\s/g, "") === "setupmanagement"
        );
        if (setupMgmt) {
          const hasDeviceSetups = (setupMgmt.children || []).some(
            (c) =>
              c.title?.toLowerCase().replace(/\s/g, "") === "devicesetups" ||
              c.id === 9920
          );
          if (!hasDeviceSetups) {
            setupMgmt.children = setupMgmt.children || [];
            setupMgmt.children.push({
              id: 9920,
              title: "Device Setups",
              to: "/device-setups",
              icon: "devicesetups",
              subrights: [
                { id: 9921, title: "Device Lock Policies" },
                { id: 9923, title: "Device Lock Actions" },
              ],
            });
          }
        }
      }

      // Asset-finance customers manage collateral through the "Collateral Asset"
      // subright (with document checklist); everyone else uses the legacy
      // "Customer Collateral" subright for unsecured-loan collateral. Both
      // resolve to the same rightId, so hide whichever one doesn't apply.
      const isAssetFinanceCustomer =
        localStorage.getItem("IsAssetFinanceCustomer") === "1";
      const isDeviceCustomer =
        localStorage.getItem("IsDeviceCustomer") === "1";

      const importedComponents =
        cleanedData
          ?.filter((child) =>
            child.children?.some(
              (subChild) => subChild.id === parseInt(rightId, 10),
            ),
          )
          .flatMap(
            (child) => {
              const currentSubChild = child.children.find((subChild) => subChild.id === parseInt(rightId, 10));
              let subrights = currentSubChild?.subrights || [];

              const isAssetFinanceSetup = subrights.some(
                (sr) => sr.title.toLowerCase().replace(/\s/g, "") === "collateralassettype"
              );

              if (isAssetFinanceSetup) {
                const hasValuerFirm = subrights.some(
                  (sr) => sr.title.toLowerCase().replace(/\s/g, "") === "valuerfirm"
                );
                const hasRegistry = subrights.some(
                  (sr) => sr.title.toLowerCase().replace(/\s/g, "") === "registry"
                );
                const hasCarModels = subrights.some(
                  (sr) => {
                    const t = sr.title.toLowerCase().replace(/\s/g, "");
                    return t === "carmodels" || t === "carmodel";
                  }
                );
                const hasCarMakes = subrights.some(
                  (sr) => {
                    const t = sr.title.toLowerCase().replace(/\s/g, "");
                    return t === "carmakes" || t === "carmake";
                  }
                );

                let updatedSubrights = [...subrights];
                const docReqIndex = updatedSubrights.findIndex(
                  (sr) => {
                    const t = sr.title.toLowerCase().replace(/\s/g, "");
                    return t === "documentrequirement" || t === "documentrequirements" || t === "assettypedocumentrequirement";
                  }
                );

                const valuerFirmSubright = { id: 9901, title: "Valuer Firm" };
                const registrySubright = { id: 9902, title: "Registry" };
                const carModelsSubright = { id: 9903, title: "Car Models" };
                const carMakesSubright = { id: 9904, title: "Car Makes" };

                if (docReqIndex !== -1) {
                  let insertOffset = 1;
                  if (!hasValuerFirm) {
                    updatedSubrights.splice(docReqIndex + insertOffset, 0, valuerFirmSubright);
                    insertOffset++;
                  }
                  if (!hasRegistry) {
                    updatedSubrights.splice(docReqIndex + insertOffset, 0, registrySubright);
                    insertOffset++;
                  }
                  if (!hasCarMakes) {
                    updatedSubrights.splice(docReqIndex + insertOffset, 0, carMakesSubright);
                    insertOffset++;
                  }
                  if (!hasCarModels) {
                    updatedSubrights.splice(docReqIndex + insertOffset, 0, carModelsSubright);
                  }
                } else {
                  if (!hasValuerFirm) updatedSubrights.push(valuerFirmSubright);
                  if (!hasRegistry) updatedSubrights.push(registrySubright);
                  if (!hasCarMakes) updatedSubrights.push(carMakesSubright);
                  if (!hasCarModels) updatedSubrights.push(carModelsSubright);
                }
                subrights = updatedSubrights;
              }

              const isCustomerManageRail = subrights.some(
                (sr) => {
                  const t = sr.title.toLowerCase().replace(/\s/g, "");
                  return t === "biodata" || t === "nextofkin" || t === "loandata";
                }
              );

              if (isCustomerManageRail) {
                const hasCustomerDevices = subrights.some(
                  (sr) => {
                    const t = sr.title.toLowerCase().replace(/\s/g, "");
                    return t === "customerdevices" || t === "devices";
                  }
                );
                if (!hasCustomerDevices) {
                  subrights.push({ id: 9905, title: "Customer Devices" });
                }
              }

              const isDeviceSetupsRail = subrights.some(
                (sr) => {
                  const t = sr.title.toLowerCase().replace(/\s/g, "");
                  return (
                    t === "devicelockpolicies" ||
                    t === "devicesetups" ||
                    t === "devicelocksetups"
                  );
                }
              );

              if (isDeviceSetupsRail) {
                const updatedDeviceSubrights = [
                  { id: 9922, title: "Device Lock Policies" },
                  { id: 9923, title: "Device Lock Actions" },
                ];
                subrights = updatedDeviceSubrights;
              }

             return subrights
                .filter((subRight) => {
                const normalizedTitle = subRight.title
                  .toLowerCase()
                  .replace(/\s/g, "");

                // Asset Finance -> hide Devices
                if (
                  isAssetFinanceCustomer &&
                  (normalizedTitle === "customerdevices" ||
                    normalizedTitle === "devices" || normalizedTitle === "customercollateral")
                ) {
                  return false;
                }

                // Device Customer -> hide Assets
                if (
                  isDeviceCustomer &&
                  (normalizedTitle === "collateralassets" || normalizedTitle === "customercollateral")
                ) {
                  return false;
                }

                return true;
              })
                .map((subRight) => {
                  const normalizedTitle = subRight.title
                    .toLowerCase()
                    .replace(/\s/g, "");

                  const componentKey = Object.keys(map).find(
                    (key) => key.toLowerCase() === normalizedTitle
                  );

                  const component = map[componentKey];

                  return {
                    label: subRight.title,
                    content: component ? React.createElement(component) : null,
                    icon: showIcons ? getIconByName(subRight.title) : null,
                  };
                }) || [];
            }
          ) || [];

      setSubRightComponents(importedComponents);
      setValue(0);
      if (importedComponents[0]?.label) {
        localStorage.setItem("SelectedTabLabel", importedComponents[0].label);
      }
    } catch (error) {
      console.error("Failed to parse rights JSON:", error);
    }
  }, [rights, rightId, map, showIcons]);

  const handleChange = (event, newValue) => {
    setValue(newValue);
    const selectedLabel = subRightComponents[newValue]?.label;
    if (selectedLabel) {
      localStorage.setItem("SelectedTabLabel", selectedLabel);
    }
  };

  const handleSelectChange = (event) => {
    handleChange(event, event.target.value);
  };

  // ─── Theme constants ───────────────────────────────────────────────────
  const cardBg = isDark ? "#162736" : "#ffffff";
  const cardBorder = isDark ? "rgba(255,255,255,0.08)" : "#e4eaf3";
  const hoverBg = isDark ? "rgba(23,102,162,0.18)" : "#e8f0fe";
  const selectedBg = isDark ? "rgba(23,102,162,0.22)" : "rgba(23,102,162,0.08)";
  const textColor = isDark ? "rgba(255,255,255,0.75)" : "#334155";

  const tabSx = (selected) => ({
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    gap: 1.1,
    minHeight: 44,
    textTransform: "none",
    fontSize: "0.86rem",
    fontWeight: selected ? 700 : 500,
    color: selected ? (isDark ? "#fff" : BRAND_BLUE) : textColor,
    bgcolor: selected ? selectedBg : "transparent",
    borderRadius: "10px",
    borderLeft: `3px solid ${selected ? BRAND_BLUE : "transparent"}`,
    mx: 1,
    my: 0.4,
    px: 1.25,
    py: 0.75,
    minWidth: 0,
    transition: "background-color 0.16s ease, color 0.16s ease",
    "&:hover": {
      bgcolor: selected ? selectedBg : hoverBg,
      color: isDark ? "#fff" : BRAND_BLUE,
    },
    "& .MuiTab-iconWrapper": { marginRight: 0, marginBottom: 0 },
  });

  const selectSx = {
    borderRadius: "10px",
    bgcolor: cardBg,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: cardBorder },
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: isCompact ? "column" : "row",
        alignItems: "flex-start",
        gap: 2,
        m: 2,
      }}
    >
      {isCompact ? (
        <Select
          value={value}
          onChange={handleSelectChange}
          fullWidth
          displayEmpty
          inputProps={{ "aria-label": "Select section" }}
          sx={selectSx}
        >
          {subRightComponents.map((tab, index) => (
            <MenuItem key={index} value={index}>
              {tab.label}
            </MenuItem>
          ))}
        </Select>
      ) : (
        <Paper
          variant="outlined"
          sx={{
            minWidth: 240,
            maxWidth: 280,
            flexShrink: 0,
            borderRadius: "14px",
            border: `1px solid ${cardBorder}`,
            bgcolor: cardBg,
            py: 1,
            position: "sticky",
            top: 16,
          }}
        >
          <Tabs
            orientation="vertical"
            variant="scrollable"
            value={value}
            onChange={handleChange}
            TabIndicatorProps={{ style: { display: "none" } }}
            sx={{ "& .MuiTabs-flexContainer": { alignItems: "stretch" } }}
          >
            {subRightComponents.map((tab, index) => (
              <Tab
                key={index}
                icon={tab.icon || undefined}
                iconPosition="start"
                label={tab.label}
                disableRipple
                {...a11yProps(index)}
                sx={tabSx(value === index)}
              />
            ))}
          </Tabs>
        </Paper>
      )}

      <Paper
        variant="outlined"
        sx={{
          flex: 1,
          width: isCompact ? "100%" : "auto",
          minHeight: 400,
          borderRadius: "14px",
          border: `1px solid ${cardBorder}`,
          bgcolor: cardBg,
          p: subRightComponents.length ? 0 : 3,
        }}
      >
        {subRightComponents.length === 0 && (
          <Typography color="text.secondary" variant="body2">
            No sections available.
          </Typography>
        )}
        {subRightComponents.map((tab, index) => (
          <TabPanel key={index} value={value} index={index}>
            {tab.content}
          </TabPanel>
        ))}
      </Paper>
    </Box>
  );
};

DynamicTabs.propTypes = {
  rightId: PropTypes.string.isRequired,
  componentsMap: PropTypes.object,
  showIcons: PropTypes.bool,
};

DynamicTabs.defaultProps = {
  componentsMap: null,
  showIcons: false,
};

export default DynamicTabs;
